import { setAccessToken } from "./redux/authSlice"

const LOGIN_HINT_KEY = 'msalLoginHint'

export const clearMsalLoginHint = () => localStorage.removeItem(LOGIN_HINT_KEY)

export const msalConfig = {
  auth: {
    clientId: "2b042489-be41-4c60-ab14-dbd0409ed670",
    authority: "https://login.microsoftonline.com/ba85c6ba-780d-434a-9c97-4a549a14ffc5", // This is a URL (e.g. https://login.microsoftonline.com/{your tenant ID})
    redirectUri: process.env.REACT_APP_DOMAIN,
    postLogoutRedirectUri: process.env.REACT_APP_DOMAIN
  },
  cache: {
    cacheLocation: "sessionStorage",
  }
};

// Add scopes here for ID token to be used at Microsoft identity platform endpoints.
export const loginRequest = {
  scopes: []
};

// Re-entrancy guard. checkMsalAuth is fired from several uncoordinated sources:
// SecureRoute's page-transition effect, its 10-minute idle interval, and — in dev —
// React StrictMode double-invoking effects. Every code path can end in an interactive
// MSAL call (ssoSilent / loginRedirect), and MSAL throws `interaction_in_progress` the
// moment a second interactive call starts before the first resolves. Because the thrown
// call falls through to *another* loginRedirect, that race turns into the login loop.
// Collapsing concurrent invocations onto a single in-flight promise removes the race at
// the source. If a path calls loginRedirect the page navigates away and the module is
// re-evaluated fresh on return, so the guard never gets stuck.
let inFlight = null

export const checkMsalAuth = (dispatch, instance, accounts, auth, location, fromPageChange) => {
  if (inFlight) { return inFlight }
  inFlight = runMsalAuth(dispatch, instance, accounts, auth, location, fromPageChange)
    .finally(() => { inFlight = null })
  return inFlight
}

const runMsalAuth = async (dispatch, instance, accounts, auth, location, fromPageChange) => {
  // The full path the user is actually trying to reach. Every redirect flow below carries this
  // so MSAL returns the user here instead of the root redirectUri (which bounces to /home and
  // drops the deep link). Includes the query string so links with params survive.
  const redirectStartPage = process.env.REACT_APP_DOMAIN + location.pathname + location.search
  const loginHint = localStorage.getItem(LOGIN_HINT_KEY)

  // No cached account (e.g. all tabs were closed and sessionStorage was cleared, or this tab was
  // opened by typing/pasting a URL so it has a fresh, empty sessionStorage).
  // Try ssoSilent with a stored loginHint so MSAL knows which Azure AD account
  // to silently re-authenticate via the SSO browser cookie.
  if (!accounts || accounts.length === 0) {
    try {
      const res = await instance.ssoSilent({ ...loginRequest, ...(loginHint && { loginHint }) })
      dispatch(setAccessToken({ accessToken: res.idToken, fromPageChange }))
    } catch (err) {
      instance.loginRedirect({ ...loginRequest, redirectStartPage })
    }
    return
  }

  // Persist the login hint so ssoSilent can target the right account after all tabs close.
  if (accounts[0]?.username) {
    localStorage.setItem(LOGIN_HINT_KEY, accounts[0].username)
  }

  const request = { ...loginRequest, account: accounts[0] }

  try {
    // @TODO: This always returns a different token for some reason
    const res = await instance.acquireTokenSilent(request)
    // Always dispatch so the `fromPageChange` flag is refreshed even when the
    // token itself didn't change. The flag gates downstream slice fetches and
    // PaymentSchedule's mount fetch; if a silent refresh persisted
    // fromPageChange=false to redux-persist and the next page-transition's
    // checkMsalAuth gets the same token back, skipping the dispatch leaves
    // the flag stuck at false — every gated effect then bails until something
    // unrelated mutates auth state. Dispatching unconditionally keeps the
    // flag correct.
    if (auth.accessToken !== res.idToken || auth.fromPageChange !== fromPageChange) {
      dispatch(setAccessToken({ accessToken: res.idToken, fromPageChange }))
    }
  } catch (err) {
    // acquireTokenSilent can fail when this tab booted from a copied-but-stale sessionStorage —
    // e.g. right-click "Open in new tab" copies the opener tab's sessionStorage, so an account is
    // present but its token artifacts are inconsistent in this isolated context. Before forcing a
    // full redirect (which would land on the root redirectUri and lose the deep link), retry via
    // ssoSilent — the same SSO-cookie path that already works for freshly opened/pasted-URL tabs.
    try {
      const res = await instance.ssoSilent({ ...loginRequest, ...(loginHint && { loginHint }), account: accounts[0] })
      dispatch(setAccessToken({ accessToken: res.idToken, fromPageChange }))
    } catch (ssoErr) {
      instance.loginRedirect({ ...loginRequest, redirectStartPage })
    }
  }
}
